import { $convertToMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableNode } from '@lexical/table';
import { $getRoot, $nodesOfType } from 'lexical';
import { useEffect, useMemo, useRef } from 'react';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { createTransformers } from '../config/transformers';
import {
  $dismissSuggestion,
  $isSuggestionNode,
  $removeSuggestionWithContent,
  $setSuggestionSource,
  $sourceSuggestion,
  SuggestionNode,
} from '../nodes/SuggestionNode';
import {
  $removeEmptyTableColumns,
  $removeEmptyTableRows,
  $setEditorValue,
  replaceImagesWithPlaceholders,
} from '../utils';
import { findScrollContainer } from '../utils/findScrollContainer';
import { isHtmlOrigin, markAsHtmlOrigin } from '../utils/tableOrigin';

export type EditorHelpers = {
  getContent: () => string;
  setContent: (markdown: string, onComplete?: () => void) => void;
  applyInlineSuggestions: () => void;
  discardSuggestions: () => void;
  setEditable: (editable: boolean) => void;
};

type EditorReadyPluginProps = {
  onEditorReady?: (helpers: EditorHelpers) => void;
  placeholderImages: MarkdownImage[];
};

export function EditorReadyPlugin({ onEditorReady, placeholderImages }: EditorReadyPluginProps) {
  const [editor] = useLexicalComposerContext();
  const transformers = useMemo(() => createTransformers(editor), [editor]);
  const onEditorReadyRef = useRef(onEditorReady);
  const placeholderImagesRef = useRef(placeholderImages);

  useEffect(() => {
    onEditorReadyRef.current = onEditorReady;
  }, [onEditorReady]);

  useEffect(() => {
    placeholderImagesRef.current = placeholderImages;
  }, [placeholderImages]);

  useEffect(() => {
    const callback = onEditorReadyRef.current;
    if (!callback) return;

    const getContent = (): string => {
      let content = '';
      editor.getEditorState().read(() => {
        const markdown = $convertToMarkdownString(transformers, undefined, true);
        content = replaceImagesWithPlaceholders(markdown, placeholderImagesRef.current);
      });
      return content;
    };

    const setContent = (markdown: string, onComplete?: () => void): void => {
      const scrollContainer = findScrollContainer(editor.getRootElement());
      const scrollTop = scrollContainer.scrollTop;

      editor.update(() => {
        $setEditorValue(markdown, transformers);
      });

      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollTop;
        onComplete?.();
      });
    };

    const applyInlineSuggestions = (): void => {
      editor.update(() => {
        const suggestionNodes = $nodesOfType(SuggestionNode);
        for (const node of suggestionNodes) {
          if (!$isSuggestionNode(node)) continue;
          const source = $sourceSuggestion(node);
          if (source === 'inline') {
            $setSuggestionSource(node, 'default');
          } else if (source === 'deletion') {
            $removeSuggestionWithContent(node);
          }
        }
        $removeEmptyTableRows();
        $removeEmptyTableColumns();
      });
    };

    const discardSuggestions = (): void => {
      const scrollContainer = findScrollContainer(editor.getRootElement());
      const scrollTop = scrollContainer.scrollTop;

      editor.update(() => {
        const suggestionNodes = $nodesOfType(SuggestionNode);
        for (const node of suggestionNodes) {
          if (!$isSuggestionNode(node)) continue;
          const source = $sourceSuggestion(node);
          if (source === 'inline') {
            $removeSuggestionWithContent(node);
          } else if (source === 'deletion') {
            $dismissSuggestion(node);
          }
        }

        // Collect HTML-origin table indices before the round-trip destroys them
        const root = $getRoot();
        const htmlOriginIndices = new Set<number>();
        let tableIdx = 0;
        for (const child of root.getChildren()) {
          if ($isTableNode(child)) {
            if (isHtmlOrigin(child)) htmlOriginIndices.add(tableIdx);
            tableIdx++;
          }
        }

        // Re-render from markdown to clear stale DOM from absolute-positioned suggestions
        const markdown = $convertToMarkdownString(transformers, undefined, true);
        $setEditorValue(markdown, transformers);

        // Restore HTML-origin markers on re-created table nodes
        if (htmlOriginIndices.size > 0) {
          const newRoot = $getRoot();
          let newTableIdx = 0;
          for (const child of newRoot.getChildren()) {
            if ($isTableNode(child)) {
              if (htmlOriginIndices.has(newTableIdx)) markAsHtmlOrigin(child);
              newTableIdx++;
            }
          }
        }
      });

      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollTop;
      });
    };

    const setEditable = (editable: boolean): void => {
      editor.setEditable(editable);
    };

    callback({ getContent, setContent, applyInlineSuggestions, discardSuggestions, setEditable });
  }, [editor, transformers]);

  return null;
}
